import React, { useState, useMemo } from "react";
import {
  AlertTriangle,
  Search,
  Calendar,
  User,
  Phone,
  Mail,
  Eye,
  X,
  ArrowLeft,
  ArrowRight,
  Filter,
  MessageSquare,
  ShieldAlert,
  Clock,
  ChevronRight
} from "lucide-react";
import { useProblemReports } from "../../Hooks/useProblemReports";
import { canAccess } from "../../utils/permissions";

export default function ProblemReports() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const { reports, isLoading, error } = useProblemReports();

  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      const searchStr = searchTerm.toLowerCase();
      return (
        r.id.toString().includes(searchStr) ||
        (r.surveyor?.full_name || "").toLowerCase().includes(searchStr) ||
        (r.surveyor?.email || "").toLowerCase().includes(searchStr) ||
        (r.report_text || "").toLowerCase().includes(searchStr)
      );
    });
  }, [reports, searchTerm]);

  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredReports.slice(start, start + pageSize);
  }, [filteredReports, currentPage]);

  const totalPages = Math.ceil(filteredReports.length / pageSize);

  if (!canAccess("view_problem_reports")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <AlertTriangle className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view problem reports.
        </p>
      </div>
    );
  }

  const stats = [
    {
      label: "Total Reports",
      value: reports.length,
      icon: ShieldAlert,
      color: "bg-amber-50 text-amber-600",
      description: "Critical issues submitted"
    },
    {
      label: "Filter Matches",
      value: filteredReports.length,
      icon: Filter,
      color: "bg-blue-50 text-blue-600",
      description: "Results in current view"
    },
    {
      label: "Latest Report",
      value: reports.length > 0 ? new Date(reports[0].created_at).toLocaleDateString() : "N/A",
      icon: Clock,
      color: "bg-purple-50 text-purple-600",
      description: "Most recent submission"
    }
  ];



  if (error) {
    return (
      <div className="p-10 text-center">
        <div className="bg-red-50 text-red-600 p-6 rounded-3xl inline-block max-w-md border border-red-100 shadow-sm">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3" />
          <p className="font-black text-lg">System Error</p>
          <p className="text-sm opacity-80 mt-1 font-medium">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen p-4 sm:p-6 lg:p-8 space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-50/50 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight">Problem Reports</h1>
          </div>
          <p className="text-gray-500 font-medium italic">Monitor and resolve critical issues reported by surveyors in the field</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-7 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
            <div className={`absolute -bottom-4 -right-4 w-24 h-24 ${stat.color} opacity-5 rounded-full transform transition-transform group-hover:scale-150 duration-700`} />
            <div className="flex items-start justify-between relative">
              <div>
                <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">{stat.label}</p>
                <p className="text-4xl font-black text-gray-900 tracking-tighter">{stat.value}</p>
                <p className="text-xs font-bold text-gray-400 mt-2 flex items-center gap-1">
                  <ChevronRight className="w-3 h-3 text-gray-300" />
                  {stat.description}
                </p>
              </div>
              <div className={`p-4 rounded-2xl ${stat.color} shadow-sm group-hover:rotate-12 transition-transform duration-300`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-5 rounded-[2rem] shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search reports by ID, surveyor name, email, or content..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-transparent focus:border-amber-400 focus:bg-white rounded-[1.25rem] outline-none transition-all text-sm font-bold placeholder:text-gray-400"
          />
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <span className="text-xs font-black text-gray-400 uppercase tracking-widest hidden lg:block">Displaying {filteredReports.length} reports</span>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden relative">
        {isLoading ? (
          <div className="p-24 text-center">
            <div className="animate-spin w-16 h-16 border-[6px] border-amber-500/20 border-t-amber-500 rounded-full mx-auto" />
            <p className="mt-6 text-gray-400 font-black tracking-widest uppercase text-xs animate-pulse">Analyzing Reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="p-24 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border-2 border-dashed border-gray-200">
              <ShieldAlert className="w-12 h-12 text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 tracking-tight">No Issues Found</h3>
            <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">All systems green! No problem reports match your current search criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Log Information</th>
                  <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Field Personnel</th>
                  <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Report Summary</th>
                  <th className="px-8 py-5 text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Timestamp</th>
                  <th className="px-8 py-5 text-center text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedReports.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50/80 transition-all group cursor-pointer"
                    onClick={() => setSelectedReport(r)}
                  >
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 font-black text-sm group-hover:scale-110 group-hover:bg-amber-100 transition-all border border-amber-100/50">
                          #{r.id}
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Entry ID</p>
                          <p className="text-sm font-bold text-gray-900">System Log</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-100">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-black text-gray-900 leading-tight">{r.surveyor?.full_name || "Unknown Surveyor"}</p>
                          <p className="text-[10px] text-blue-600 font-bold uppercase tracking-tight mt-0.5">{r.surveyor?.email || "No Email Provided"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 max-w-xs xl:max-w-md">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <MessageSquare className="w-4 h-4 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600 line-clamp-2 leading-relaxed italic">
                          "{r.report_text}"
                        </p>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Calendar className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-900">
                            {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                            {new Date(r.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <button className="w-10 h-10 bg-white rounded-xl border border-gray-200 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:border-amber-200 hover:shadow-lg transition-all mx-auto">
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="p-8 border-t border-gray-50 bg-gray-50/30 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-white rounded-xl border border-gray-200 shadow-sm text-xs font-black text-gray-500 uppercase tracking-[0.2em]">
                Page {currentPage} of {totalPages}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-3 bg-white border border-gray-200 rounded-2xl text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed hover:text-gray-900 transition-all shadow-sm hover:shadow active:scale-95"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${currentPage === i + 1 ? 'bg-gray-900 text-white shadow-lg shadow-gray-200 scale-110' : 'bg-white text-gray-400 hover:bg-gray-100 border border-gray-100'}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-3 bg-white border border-gray-200 rounded-2xl text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed hover:text-gray-900 transition-all shadow-sm hover:shadow active:scale-95"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedReport && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] shadow-2xl max-w-3xl w-full overflow-hidden border border-white/20 relative">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 text-white relative">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-black uppercase tracking-[0.3em] opacity-60">Critical Field Report</span>
                </div>
                <button onClick={() => setSelectedReport(null)} className="p-2 bg-white/10 hover:bg-white/20 rounded-2xl transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <h2 className="text-3xl font-black tracking-tight">Report Log #00{selectedReport.id}</h2>
            </div>

            <div className="p-8 sm:p-10 space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-50 rounded-[1.25rem] flex items-center justify-center">
                      <User className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Field Surveyor</p>
                      <p className="text-xl font-black text-gray-900 leading-none">{selectedReport.surveyor?.full_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-50 rounded-[1.25rem] flex items-center justify-center">
                      <Clock className="w-7 h-7 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">Incident Timestamp</p>
                      <p className="text-lg font-black text-gray-900 leading-none">
                        {new Date(selectedReport.created_at).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 bg-gray-50 p-6 rounded-[2rem] border border-gray-100">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Contact Linkage</p>
                  <div className="flex items-center gap-3 text-sm font-bold text-gray-700">
                    <Mail className="w-4 h-4 text-blue-500" />
                    {selectedReport.surveyor?.email}
                  </div>
                  <div className="flex items-center gap-3 text-sm font-bold text-gray-700">
                    <Phone className="w-4 h-4 text-emerald-500" />
                    {selectedReport.surveyor?.phone}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-amber-500" />
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Full Narrative Report</h3>
                </div>
                <div className="bg-gray-50 p-8 rounded-[2.5rem] border border-gray-100 shadow-inner">
                  <p className="text-gray-800 leading-[1.8] font-medium text-lg italic font-serif">
                    “{selectedReport.report_text || "No descriptive narrative provided for this entry."}”
                  </p>
                </div>
              </div>
            </div>

            <div className="px-10 py-8 bg-gray-50/50 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedReport(null)}
                className="px-8 py-4 bg-gray-900 text-white rounded-[1.25rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 active:scale-95"
              >
                Acknowledge Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}